import { useCallback } from 'react';

import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { EVENT_CHAIN_STATUS, type EventChainStatus } from 'cpool-constants';

import { eventChainSocket } from '~/api/eventChainSocket';

const DEFAULT_TIMEOUT_MS = 15_000;

/** How the chain resolved: the backend's status, or `'timeout'` if the completion push never arrived. */
export type ChainCompletionStatus = EventChainStatus;

type ChainInvalidation = {
    /** The `id` returned by `sendCommand` (the chain's correlation id). */
    correlationId: string;
    /**
     * Called once the chain resolves — with the backend `status` (`'completed'`/`'failed'`) or
     * `'timeout'` if no push arrived within `timeoutMs`. Runs after the invalidation. Lets a caller
     * react to completion (e.g. show a toast). Note the status is the *chain's* outcome (did a job
     * fail), not the command's business outcome.
     */
    onComplete?: (status: ChainCompletionStatus) => void;
    /** React Query keys to invalidate once the chain completes. */
    queryKeys?: QueryKey[];
    /** How long to wait for the completion push before giving up on it (default 15s). */
    timeoutMs?: number;
};

/**
 * Invalidation for a command mutation that is **expected to kick off an event chain**. It waits for
 * the backend to report the chain complete (KC-6442) and
 * invalidates the queries — so the UI refetches the chain's final projected state, never an
 * intermediate one.
 *
 * There is deliberately no pre-completion refetch: because `position` is part of a query's cache key,
 * advancing it (or invalidating) before the chain finishes would refetch an intermediate projection.
 * If the completion push does not arrive within `timeoutMs`, it invalidates once anyway as a
 * best-effort fallback so a missed/slow push cannot leave data stale.
 *
 * Only use this for chain-producing commands; a plain single-event command is better served by the
 * usual immediate `setQueryPosition` + `invalidateQueries` in `onSuccess`.
 *
 * Pass a single spec for the common one-command case, or an array of specs when a mutation issues
 * several commands — each spec has its own correlation id and its own
 * invalidation set.
 */
export const useChainAwareInvalidation = () => {
    const queryClient = useQueryClient();

    return useCallback(
        (invalidations: ChainInvalidation | ChainInvalidation[]) => {
            const specs = Array.isArray(invalidations) ? invalidations : [invalidations];

            specs.forEach(({ correlationId, onComplete, queryKeys = [], timeoutMs = DEFAULT_TIMEOUT_MS }) => {
                const hasWork = queryKeys.length > 0 || onComplete != null;
                if (!hasWork) return;

                const invalidate = () => queryKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));

                // Only invalidate once the chain is confirmed complete, so the
                // refetch reflects the final state. `waitForCompletion` also resolves on timeout, so a
                // missed/slow push still refreshes once (best effort) instead of stranding stale data.
                eventChainSocket
                    .waitForCompletion(correlationId, { timeoutMs })
                    .then((message) => {
                        invalidate();
                        onComplete?.(message ? message.status : EVENT_CHAIN_STATUS.TIMEOUT);
                    })
                    .catch(() => {
                        // waitForCompletion never rejects; guard only to satisfy no-floating-promises
                    });
            });
        },
        [queryClient],
    );
};
